/**
 * Command class allows us to instantiate and execute methods from other imports
 *
 * The Command pattern aims to encapsulate method invocation, requests, or
 * operations into a single object and gives us the ability to both parameterize
 * and pass method calls around that can be executed at our discretion.
 *
 * In addition, it enables us to decouple objects invoking the action from the
 * objects that implement them, giving us a greater degree of overall
 * flexibility in swapping out concrete classes or methods (objects).
 *
 * @export
 * @class Command
 */
export class Command {
  configuration: Record<string, unknown>;
  authentication: Record<string, unknown>;
  user: Record<string, unknown>;
  tokens: Record<string, unknown>;
  /**
   *Creates an instance of Command. Typical usage would be to use with acc-boot
   *or other credentials systems to set the configuration and authentication
   *params
   * @param {Object} configuration
   * @param {Object} authentication
   * @param {Object} user
   * @memberof Command
   */
  constructor(
    configuration: Record<string, unknown>,
    authentication: Record<string, unknown>,
    user: Record<string, unknown>,
    tokens: Record<string, unknown>,
  ) {
    this.configuration = configuration;
    this.authentication = authentication;
    this.user = user;
    this.tokens = tokens;
  }

  /**
   * The executor which takes a function pass and the parameters of that function
   * and applies the existing state of the class to the function
   *
   * @param {function} func
   * @param {*} params
   * @returns
   * @memberof Command
   */
  async execute<T>(func: () => Promise<T>, ...params: []): Promise<T> {
    return func.apply(this, params);
  }

  /**
   * User object setter for potential changes during life an instantiation
   *
   * @param {Object} user
   * @memberof Command
   */
  setUser(user: Record<string, unknown>): void {
    this.user = user;
  }

  /**
   * Authentication object setter for potential changes during life an
   * instantiation
   *
   * @param {Object} auth
   * @memberof Command
   */
  setAuthentication(auth: Record<string, unknown>): void {
    this.authentication = auth;
  }

  /**
   * Configuration object setter for potential changes during life an
   * instantiation
   *
   * @param {Object} config
   * @memberof Command
   */
  setConfiguration(config: Record<string, unknown>): void {
    this.configuration = config;
  }

  /**
   * Tokens object setter for potential changes during life an
   * instantiation
   *
   * @param {Object} tokens
   * @memberof Command
   */
  setTokens(tokens: Record<string, unknown>): void {
    this.tokens = tokens;
  }
}
